import { useState, useEffect } from 'react';
import {
  IconChevronDown,
  IconChevronUp,
  IconCircleCheckFilled,
  IconCircleXFilled,
  IconClockFilled,
  IconPlayerPlayFilled,
  IconFileDownloadFilled,
  IconHome2Filled,
  IconArchiveFilled,
  IconMotorbikeFilled,
} from '@tabler/icons-react';
import { apiFetch } from '../api/client';

const FONT = '"Public Sans", sans-serif';
const PANEL = { backgroundColor: 'rgba(255, 255, 255, 0.08)' };

const typeLabels = {
  in_store: 'En tienda',
  pickup: 'Pickup',
  delivery: 'Domicilio',
};

const statusLabels = {
  pending: 'Pendiente',
  in_progress: 'En progreso',
  ready: 'Listo',
  completed: 'Completado',
  cancelled: 'Cancelado',
};

const typeBadgeConfig = {
  in_store: { color: '#0a84ff', icon: IconHome2Filled },
  pickup: { color: '#8e8e93', icon: IconArchiveFilled },
  delivery: { color: '#30d158', icon: IconMotorbikeFilled },
};

const statusBadgeConfig = {
  pending: { color: '#f87c2b', icon: IconClockFilled },
  in_progress: { color: '#0a84ff', icon: IconPlayerPlayFilled },
  ready: { color: '#30d158', icon: IconCircleCheckFilled },
  completed: { color: '#30d158', icon: IconCircleCheckFilled },
  cancelled: { color: '#ff453a', icon: IconCircleXFilled },
};

function formatMoney(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('es-MX');
}

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
}

// asyncpg devuelve jsonb como string; normalizar a lista.
function getAppliedPromos(sale) {
  let promos = sale?.applied_promotions;
  if (typeof promos === 'string') {
    try {
      promos = JSON.parse(promos);
    } catch {
      promos = [];
    }
  }
  return Array.isArray(promos) ? promos : [];
}

function Panel({ children, className = '' }) {
  return (
    <div className={`rounded-3xl p-4 ${className}`} style={PANEL}>
      {children}
    </div>
  );
}

function PanelTitle({ children, aside }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <h3
        className="font-semibold m-0"
        style={{ fontSize: '14px', lineHeight: '20px', color: 'rgba(255, 255, 255, 0.9)', fontFamily: FONT }}
      >
        {children}
      </h3>
      {aside && (
        <span
          style={{
            fontSize: '12px',
            lineHeight: '16px',
            fontWeight: 600,
            color: 'rgba(255, 255, 255, 0.35)',
            fontFamily: FONT,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {aside}
        </span>
      )}
    </div>
  );
}

function Badge({ icon: Icon, color, children }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 h-8 rounded-full px-3"
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.06)',
        fontSize: '13px',
        lineHeight: '18px',
        fontWeight: 600,
        color: 'rgba(255, 255, 255, 0.82)',
        fontFamily: FONT,
      }}
    >
      <Icon size={14} color={color} />
      {children}
    </span>
  );
}

function TotalRow({ label, value, color = 'rgba(255, 255, 255, 0.9)', strong = false }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span
        className="truncate"
        style={{
          fontSize: strong ? '14px' : '13px',
          lineHeight: '20px',
          fontWeight: strong ? 600 : 500,
          color: strong ? 'rgba(255, 255, 255, 0.6)' : 'rgba(255, 255, 255, 0.45)',
          fontFamily: FONT,
        }}
      >
        {label}
      </span>
      <span
        className="shrink-0"
        style={{
          fontSize: strong ? '17px' : '13px',
          lineHeight: '20px',
          fontWeight: strong ? 600 : 500,
          color,
          fontFamily: FONT,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: strong ? '-0.01em' : undefined,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function PedidoSkeleton() {
  return (
    <div className="flex flex-col gap-5" role="status" aria-label="Cargando pedido">
      <div className="flex gap-2">
        {[64, 88, 96].map((w) => (
          <span
            key={w}
            className="h-8 rounded-full animate-pulse"
            style={{ width: w, backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
          />
        ))}
      </div>
      <div className="flex flex-col gap-2">
        <span className="h-5 w-40 rounded-full animate-pulse" style={{ backgroundColor: 'rgba(255, 255, 255, 0.06)' }} />
        <span className="h-3 w-24 rounded-full animate-pulse" style={{ backgroundColor: 'rgba(255, 255, 255, 0.04)' }} />
      </div>
      <div className="flex flex-col gap-3.5">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center justify-between gap-4">
            <span
              className="h-3.5 rounded-full animate-pulse"
              style={{ width: `${48 - i * 6}%`, backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
            />
            <span className="h-3.5 w-14 rounded-full animate-pulse" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ModalDetallesPedido({ isOpen, saleId, onClose }) {
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());

  useEffect(() => {
    if (!isOpen || !saleId) {
      setSale(null);
      setExpandedItems(new Set());
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await apiFetch(`/sales/${saleId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setSale(data);
      } catch {
        if (!cancelled) setSale(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, saleId]);

  const toggleItem = (itemId) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  if (!isOpen) return null;

  const typeLabel = typeLabels[sale?.sale_type] || sale?.sale_type || '';
  const typeConfig = typeBadgeConfig[sale?.sale_type] || typeBadgeConfig.in_store;
  const TypeIcon = typeConfig.icon;
  const statusLabel = statusLabels[sale?.status] || sale?.status || '';
  const statusConfig = statusBadgeConfig[sale?.status] || statusBadgeConfig.pending;
  const StatusIcon = statusConfig.icon;
  const items = sale?.items || [];
  const appliedPromos = getAppliedPromos(sale);
  const promoName = appliedPromos[0]?.name || '';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-3"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.72)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Detalles del pedido"
        className="flex flex-col overflow-hidden w-full"
        style={{
          backgroundColor: '#000',
          borderRadius: '28px',
          border: '1px solid rgba(255, 255, 255, 0.16)',
          maxWidth: '960px',
          maxHeight: 'min(780px, calc(100dvh - 24px))',
          boxShadow: '0 40px 120px rgba(0, 0, 0, 0.8)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-5 pt-5 pb-4 sm:px-6 sm:pt-6 border-b border-white/[0.16]">
          <h2
            className="font-semibold m-0 truncate"
            style={{
              fontSize: '22px',
              lineHeight: '28px',
              letterSpacing: '-0.02em',
              color: 'rgba(255, 255, 255, 0.95)',
              fontFamily: FONT,
            }}
          >
            Detalles del pedido
          </h2>
          <p
            className="m-0 mt-1 truncate"
            style={{ fontSize: '13px', lineHeight: '18px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.45)', fontFamily: FONT }}
          >
            {sale?.code_order || 'Artículos, cliente y cobro'}
          </p>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto pedidos-scroll px-4 sm:px-6 pt-5 pb-5">
          {loading ? (
            <PedidoSkeleton />
          ) : !sale ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <span
                className="flex items-center justify-center w-12 h-12 rounded-2xl"
                style={{ backgroundColor: 'rgba(255, 69, 58, 0.1)' }}
              >
                <IconCircleXFilled size={24} color="#ff453a" style={{ opacity: 0.8 }} />
              </span>
              <p className="m-0" style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.4)', fontFamily: FONT }}>
                No se pudo cargar el pedido.
              </p>
            </div>
          ) : (
            <div className="flex flex-col min-[900px]:flex-row gap-4 min-[900px]:gap-6">
              {/* Columna izquierda: resumen y cobro */}
              <div className="flex flex-col gap-4 w-full min-[900px]:w-[300px] shrink-0 min-[900px]:border-r min-[900px]:border-white/[0.16] min-[900px]:pr-6">
                <Panel>
                  <PanelTitle>Resumen</PanelTitle>
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <Badge icon={TypeIcon} color={typeConfig.color}>
                      {typeLabel}
                    </Badge>
                    <Badge icon={StatusIcon} color={statusConfig.color}>
                      {statusLabel}
                    </Badge>
                    <Badge
                      icon={IconFileDownloadFilled}
                      color={sale.requires_invoice ? '#0a84ff' : 'rgba(255, 255, 255, 0.4)'}
                    >
                      {sale.requires_invoice ? 'Factura' : 'Sin factura'}
                    </Badge>
                  </div>

                  <div className="mt-4">
                    <h3
                      className="font-semibold m-0 truncate"
                      style={{
                        fontSize: '17px',
                        lineHeight: '22px',
                        letterSpacing: '-0.01em',
                        color: 'rgba(255, 255, 255, 0.95)',
                        fontFamily: FONT,
                      }}
                    >
                      {sale.customer_name || 'Cliente mostrador'}
                    </h3>
                    {sale.customer_tel && (
                      <p
                        className="m-0 mt-1 truncate"
                        style={{ fontSize: '13px', lineHeight: '18px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.45)', fontFamily: FONT }}
                      >
                        {sale.customer_tel}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 mt-4">
                    <TotalRow label="Fecha" value={formatDate(sale.sale_date)} color="rgba(255, 255, 255, 0.8)" />
                    <TotalRow label="Hora" value={formatTime(sale.sale_date)} color="rgba(255, 255, 255, 0.8)" />
                  </div>
                </Panel>

                <Panel>
                  <PanelTitle>Cobro</PanelTitle>
                  <div className="flex flex-col gap-2 mt-3">
                    <TotalRow label="Subtotal" value={formatMoney(sale.subtotal)} color="rgba(255, 255, 255, 0.8)" />
                    {Number(sale.discount_amount) > 0 && (
                      <TotalRow
                        label={`Descuento${promoName ? ` — ${promoName}` : ''}`}
                        value={`-${formatMoney(sale.discount_amount)}`}
                        color="#30d158"
                      />
                    )}
                    {Number(sale.points_discount) > 0 && (
                      <TotalRow
                        label="Descuento por puntos"
                        value={`-${formatMoney(sale.points_discount)}`}
                        color="#30d158"
                      />
                    )}
                    {Number(sale.tax_amount) > 0 && (
                      <TotalRow label="IVA" value={`-${formatMoney(sale.tax_amount)}`} color="#f87c2b" />
                    )}
                    {Number(sale.mp_commission_amount) > 0 && (
                      <TotalRow
                        label={`Comisión ${sale.payment_method === 'debit' ? 'débito' : sale.payment_method === 'credit' ? 'crédito' : 'tarjeta'}`}
                        value={`-${formatMoney(sale.mp_commission_amount)}`}
                        color="#f87c2b"
                      />
                    )}
                    <div className="pt-2">
                      <TotalRow
                        strong
                        label="Total neto"
                        value={formatMoney(sale.total_amount)}
                        color="rgba(255, 255, 255, 0.95)"
                      />
                    </div>
                  </div>
                </Panel>
              </div>

              {/* Columna derecha: artículos y notas */}
              <div className="flex flex-col gap-5 flex-1 min-w-0">
                <section>
                  <PanelTitle aside={items.length}>Artículos</PanelTitle>

                  {items.length === 0 ? (
                    <p className="m-0 mt-3" style={{ fontSize: '14px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.4)', fontFamily: FONT }}>
                      Este pedido no tiene artículos.
                    </p>
                  ) : (
                    <div className="flex flex-col gap-1 mt-2">
                      {items.map((item) => {
                        const hasVariants = (item.variants || []).length > 0;
                        const isExpanded = expandedItems.has(item.id);
                        return (
                          <div key={item.id} className="flex flex-col">
                            <button
                              type="button"
                              onClick={() => toggleItem(item.id)}
                              disabled={!hasVariants}
                              aria-expanded={hasVariants ? isExpanded : undefined}
                              className={`flex items-center justify-between gap-3 w-full min-h-[52px] px-4 py-2.5 rounded-2xl border-none text-left transition-colors duration-200 ease-out ${
                                hasVariants ? 'cursor-pointer [@media(hover:hover)]:hover:bg-white/[0.1]' : 'cursor-default'
                              }`}
                              style={{ backgroundColor: 'rgba(255, 255, 255, 0.06)' }}
                            >
                              <span className="flex items-center gap-2 min-w-0">
                                <span
                                  className="truncate"
                                  style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.9)', fontFamily: FONT }}
                                >
                                  <span style={{ color: 'rgba(255, 255, 255, 0.4)', fontWeight: 600 }}>
                                    {item.quantity}×
                                  </span>{' '}
                                  {item.item_name}
                                </span>
                              </span>
                              <span className="flex items-center gap-3 shrink-0">
                                <span
                                  style={{
                                    fontSize: '14px',
                                    lineHeight: '20px',
                                    fontWeight: 600,
                                    color: 'rgba(255, 255, 255, 0.95)',
                                    fontFamily: FONT,
                                    fontVariantNumeric: 'tabular-nums',
                                  }}
                                >
                                  {formatMoney(item.line_total)}
                                </span>
                                {hasVariants && (
                                  <span
                                    className="flex items-center justify-center shrink-0"
                                    style={{
                                      width: '28px',
                                      height: '28px',
                                      borderRadius: '9999px',
                                      backgroundColor: '#fff',
                                    }}
                                  >
                                    {isExpanded ? (
                                      <IconChevronUp size={16} color="#000" />
                                    ) : (
                                      <IconChevronDown size={16} color="#000" />
                                    )}
                                  </span>
                                )}
                              </span>
                            </button>
                            {hasVariants && isExpanded && (
                              <div
                                className="flex flex-col gap-2 mt-1 mb-2 ml-2 rounded-2xl p-3"
                                style={{ backgroundColor: 'rgba(255, 255, 255, 0.04)' }}
                              >
                                {(item.variants || []).map((v, i) => (
                                  <div key={i} className="flex items-center justify-between gap-3">
                                    <span
                                      style={{ fontSize: '12px', lineHeight: '16px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.4)', fontFamily: FONT, letterSpacing: '0.01em' }}
                                    >
                                      {v.group_name}
                                    </span>
                                    <span
                                      className="inline-flex items-center justify-center rounded-full px-3"
                                      style={{
                                        height: '24px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                        fontSize: '12px',
                                        lineHeight: '16px',
                                        fontWeight: 600,
                                        color: 'rgba(255, 255, 255, 0.85)',
                                        fontFamily: FONT,
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {v.option_name}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>

                {sale.notes && (
                  <section>
                    <PanelTitle>Notas</PanelTitle>
                    <p className="m-0 mt-2" style={{ fontSize: '14px', lineHeight: '21px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.9)', fontFamily: FONT }}>
                      {sale.notes}
                    </p>
                  </section>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end shrink-0 px-4 sm:px-6 py-4 sm:py-5 border-t border-white/[0.16]">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center w-full sm:w-auto h-12 px-6 rounded-full border-none cursor-pointer font-semibold transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-white/[0.85]"
            style={{
              backgroundColor: '#fff',
              color: '#000',
              fontSize: '14px',
              letterSpacing: '-0.01em',
              fontFamily: FONT,
            }}
          >
            Regresar
          </button>
        </div>
      </div>
    </div>
  );
}
